import { useCallback, useEffect, useMemo, useRef, useState } from "react"

import { useUser } from "@clerk/nextjs"
import { toast } from "@coval/ui"

import { useConversationsFilter } from "../contexts/ConversationsFilterContext"
import { useConversationsSavedViewsTransport } from "../routes/useConversationsSavedViewsTransport"
import { viewConfigBuilder } from "../utils/viewConfigBuilder"

interface UseSavedViewsStateProps {
  visibleColumns: Set<string>
}

export const useSavedViewsState = ({
  visibleColumns,
}: UseSavedViewsStateProps) => {
  const { user } = useUser()

  const {
    state: filterState,
    loadedViewId,
    loadView,
    clearView,
    detachView,
  } = useConversationsFilter()

  const [editingViewId, setEditingViewId] = useState<string | null>(null)
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null)
  const [viewName, setViewName] = useState("")
  const [viewDescription, setViewDescription] = useState("")
  const [isShared, setIsShared] = useState(true)

  const {
    createView: createViewTransport,
    deleteView: deleteViewTransport,
    isLoadingViews,
    savedViews,
    updateView: updateViewTransport,
  } = useConversationsSavedViewsTransport()

  // ── Validate loadedViewId against savedViews ──────────────
  // If the context set a loadedViewId from localStorage but the view
  // was deleted, clear it. If the view exists and the context only set
  // the ID (last-view fallback without auto-saved config), apply the
  // DB config now.

  const hasValidatedRef = useRef(false)

  useEffect(() => {
    if (!savedViews || hasValidatedRef.current || !loadedViewId) {
      return
    }
    hasValidatedRef.current = true

    const view = savedViews.find((v) => v.ulid === loadedViewId)
    if (!view) {
      // View was deleted — silently detach without resetting filters/columns
      detachView()
    }
    // If the view exists, the context already applied the auto-saved config
    // or the DB config will be applied via loadView by the user.
  }, [savedViews, loadedViewId, detachView])

  // ── Config builders ────────────────────────────────────────

  const handleBuildCurrentConfig = useCallback(() => {
    return viewConfigBuilder.buildCurrentConfig({
      filterState,
      visibleColumns,
      viewDescription,
      isShared,
      userEmail: user?.emailAddresses[0].emailAddress || "",
    })
  }, [filterState, visibleColumns, viewDescription, isShared, user])

  // ── Actions ────────────────────────────────────────────────

  const handleResetForm = useCallback(() => {
    setViewName("")
    setViewDescription("")
    setIsShared(true)
  }, [])

  // ── CRUD mutations ─────────────────────────────────────────

  const createView = useMemo(
    () => ({
      ...createViewTransport,
      mutateAsync: async (
        input: Parameters<typeof createViewTransport.mutateAsync>[0],
      ) => {
        try {
          const result = await createViewTransport.mutateAsync(input)
          toast.success({
            title: "View saved",
            description: `"${input.name}" has been saved successfully.`,
          })
          handleResetForm()
          return result
        } catch (error) {
          toast.error({
            title: "Error saving view",
            error,
            fallback: "Could not save the view.",
          })
          throw error
        }
      },
    }),
    [createViewTransport, handleResetForm],
  )

  const updateView = useMemo(
    () => ({
      ...updateViewTransport,
      mutateAsync: async (
        input: Parameters<typeof updateViewTransport.mutateAsync>[0],
      ) => {
        try {
          const result = await updateViewTransport.mutateAsync(input)
          toast.success({
            title: "View updated",
            description: "The view has been updated successfully.",
          })
          setEditingViewId(null)
          return result
        } catch (error) {
          toast.error({
            title: "Error updating view",
            error,
            fallback: "Could not update the view.",
          })
          throw error
        }
      },
    }),
    [updateViewTransport],
  )

  // Rejects on failure without a toast, so the view's name field stays open and shows why.
  const handleRenameView = useCallback(
    async (viewUlid: string, name: string) => {
      const trimmed = name.trim()
      if (!trimmed) {
        return
      }
      await updateViewTransport.mutateAsync({ ulid: viewUlid, name: trimmed })
      toast.success({
        title: "View updated",
        description: "The view has been updated successfully.",
      })
      setEditingViewId(null)
    },
    [updateViewTransport],
  )

  const deleteView = useMemo(
    () => ({
      ...deleteViewTransport,
      mutate: (input: Parameters<typeof deleteViewTransport.mutate>[0]) => {
        deleteViewTransport.mutate(input, {
          onError: (error) => {
            toast.error({
              title: "Error deleting view",
              error,
              fallback: "Could not delete the view.",
            })
          },
          onSuccess: () => {
            toast.success({
              title: "View deleted",
              description: "The view has been deleted successfully.",
            })
            setConfirmDeleteId(null)
            if (loadedViewId === confirmDeleteId) {
              clearView()
            }
          },
        })
      },
    }),
    [clearView, confirmDeleteId, deleteViewTransport, loadedViewId],
  )

  const handleSaveCurrentView = useCallback(async () => {
    const name = viewName.trim()
    if (!name) {
      toast.danger({
        title: "Name required",
        description: "Please enter a name for the view.",
      })
      return
    }

    try {
      await createView.mutateAsync({
        name,
        config: handleBuildCurrentConfig(),
      })
    } catch {
      // createView already showed the failure; the form keeps its values for a retry.
    }
  }, [viewName, handleBuildCurrentConfig, createView])

  // Overwrites the loaded view's filter and display fields with the current
  // ones; its name, sharing, description, and any field the current state
  // does not carry (such as timestamp mode) stay as saved.
  const handleUpdateExistingView = useCallback(async () => {
    const loadedView = savedViews?.find((v) => v.ulid === loadedViewId)
    if (!loadedView) {
      return
    }

    const currentConfig = handleBuildCurrentConfig()
    try {
      await updateView.mutateAsync({
        ulid: loadedView.ulid,
        config: {
          ...loadedView.config,
          filters: { ...loadedView.config.filters, ...currentConfig.filters },
          display: { ...loadedView.config.display, ...currentConfig.display },
        },
      })
    } catch {
      // updateView already showed the failure.
    }
  }, [loadedViewId, savedViews, handleBuildCurrentConfig, updateView])

  const hasChangesFromLoadedView = useCallback(() => {
    if (!loadedViewId || !savedViews) {
      return false
    }

    const loadedView = savedViews.find((v) => v.ulid === loadedViewId)
    if (!loadedView) {
      return false
    }

    const currentConfig = handleBuildCurrentConfig()

    // Normalize arrays: treat undefined/null as [] for stable comparison
    const norm = (val: unknown) => JSON.stringify(val ?? [])
    // Normalize scalars: treat undefined/null as empty string
    const normStr = (val: unknown) => val ?? ""

    const hasFilterChanges =
      norm(currentConfig.filters?.metricIds) !==
        norm(loadedView.config.filters?.metricIds) ||
      norm(currentConfig.filters?.agentIds) !==
        norm(loadedView.config.filters?.agentIds) ||
      norm(currentConfig.filters?.tagFilters) !==
        norm(loadedView.config.filters?.tagFilters) ||
      norm(currentConfig.filters?.projectIds) !==
        norm(loadedView.config.filters?.projectIds) ||
      norm(currentConfig.filters?.metadataFilters) !==
        norm(loadedView.config.filters?.metadataFilters) ||
      normStr(currentConfig.filters?.startDate) !==
        normStr(loadedView.config.filters?.startDate) ||
      normStr(currentConfig.filters?.endDate) !==
        normStr(loadedView.config.filters?.endDate) ||
      norm(currentConfig.filters?.metricValueFilters) !==
        norm(loadedView.config.filters?.metricValueFilters)

    // For visibleColumns, only compare columns that were in the saved view.
    // Auto-added metric columns (from data loading) shouldn't count as
    // user modifications — the view never had an opinion about them.
    const savedColumns = new Set(
      loadedView.config.display?.visibleColumns ?? [],
    )
    const currentColumnsFiltered = (
      currentConfig.display?.visibleColumns ?? []
    ).filter((col) => savedColumns.has(col))
    const hasColumnChanges =
      norm(currentColumnsFiltered) !==
      norm(loadedView.config.display?.visibleColumns)

    const hasDisplayChanges =
      normStr(currentConfig.display?.sortColumn) !==
        normStr(loadedView.config.display?.sortColumn) ||
      normStr(currentConfig.display?.sortDirection) !==
        normStr(loadedView.config.display?.sortDirection) ||
      hasColumnChanges

    return hasFilterChanges || hasDisplayChanges
  }, [loadedViewId, savedViews, handleBuildCurrentConfig])

  // ── Grouped views ──────────────────────────────────────────

  const groupedViews = useMemo(() => {
    if (!savedViews || !user) {
      return { orgViews: [], myViews: [] }
    }

    const orgViews = savedViews.filter(
      (view) => view.config.metadata?.isShared === true,
    )
    const myViews = savedViews.filter(
      (view) =>
        view.config.metadata?.isShared === false && view.userId === user.id,
    )

    return { orgViews, myViews }
  }, [savedViews, user])

  return {
    state: {
      editingViewId,
      confirmDeleteId,
      viewName,
      viewDescription,
      isShared,
    },
    actions: {
      setEditingViewId,
      setConfirmDeleteId,
      setViewName,
      setViewDescription,
      setIsShared,
      handleLoadView: loadView,
      handleClearView: clearView,
      handleResetForm,
      handleSaveCurrentView,
      handleUpdateExistingView,
      handleRenameView,
      hasChangesFromLoadedView,
    },
    data: {
      savedViews,
      isLoadingViews,
      groupedViews,
    },
    mutations: {
      createView,
      updateView,
      deleteView,
    },
  }
}
